import {
  ColDef,
  ColGroupDef,
  Column,
  FloatingFilterUiChangedEvent,
  GridApi,
  GridOptions,
  ICellRendererParams,
  ProcessUnpinnedColumnsParams,
  SizeColumnsToContentStrategy,
} from 'ag-grid-community';
import { reverse } from 'lodash';
import { useMemo } from 'react';

import { DB_DATE_FORMAT, DEFAULT_PAGE_SIZE, DEFAULT_ROW_HEIGHT } from 'core/constants';
import { AgGridDataRow } from 'core/types';

import { formatDate } from './date';

export const defaultShouldRowBeUpdated = (newRow: AgGridDataRow, existingRow: AgGridDataRow) => {
  if (!newRow?.updatedAt && !existingRow?.updatedAt) {
    return newRow !== existingRow;
  }

  return newRow?.updatedAt > existingRow?.updatedAt;
};

// A leaf whose single-child group was removed via groupRemoveSingleChildren — visually it
// stands in for the group row, so parent-level cell renderers should still paint on it.
export const isPromotedSingleChild = (params: { node?: ICellRendererParams['node'] }) =>
  !params.node?.group &&
  !!params.node?.parent?.group &&
  params.node?.parent?.allLeafChildren?.length === 1;

export const getAgGridTableCellData = (params: ICellRendererParams, colId: string) => {
  const {
    data,
    node: { childrenAfterGroup, rowGroupColumn },
  } = params;

  // If the ag grid is not grouped by any column,
  // return the data (data is not available when ag grid is grouped).
  if (data) {
    return data;
  }

  // Get the data for the grouped row; when the ag grid has grouped active, the data for the grouped
  // row is in the childrenAfterGroup.

  // If the ag grid is grouped by current column,
  // return the data in order to display the "grouped column" the actual value
  const isGroupedByColumn = colId === rowGroupColumn?.getColId();

  if (childrenAfterGroup && isGroupedByColumn) {
    return childrenAfterGroup?.[0]?.childrenAfterGroup
      ? getAgGridTableCellData(
          { data: null, node: childrenAfterGroup[0] } as ICellRendererParams,
          colId
        )
      : childrenAfterGroup?.[0]?.data;
  }

  // If the ag grid is grouped by other column,
  // return no data, so no value displayed.
  return null;
};

export interface ICustomGridOptionsParams extends GridOptions {
  pageSize?: number;
  hasRowReordering?: boolean;
  autoSizeStrategy?: SizeColumnsToContentStrategy;
}

export const useGetGridOptions = ({
  rowModelType = 'clientSide',
  pageSize = DEFAULT_PAGE_SIZE,
  rowHeight = DEFAULT_ROW_HEIGHT,
  statusBar = null,
  hasRowReordering = false,
  onRowDragEnter,
  onRowDragMove,
  onRowDragEnd,
  onFilterChanged,
  ...rest
}: ICustomGridOptionsParams): GridOptions => {
  const sideBar = useMemo(
    () => ({
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
        },
        {
          id: 'filters',
          labelDefault: 'Filters',
          labelKey: 'filters',
          iconKey: 'filter',
          toolPanel: 'agFiltersToolPanel',
        },
      ],
      defaultToolPanel: '',
    }),
    []
  );

  return {
    columnMenu: 'new',
    maxConcurrentDatasourceRequests: rowModelType === 'clientSide' ? 1 : undefined,
    infiniteInitialRowCount: pageSize,
    rowModelType,
    animateRows: false,
    sideBar,
    rowHeight,
    multiSortKey: 'ctrl',
    tooltipMouseTrack: true,
    tooltipShowDelay: 0,
    statusBar,
    rowDragManaged: hasRowReordering,
    onRowDragEnter: hasRowReordering ? onRowDragEnter : null,
    onRowDragMove: hasRowReordering ? onRowDragMove : null,
    onRowDragEnd: hasRowReordering ? onRowDragEnd : null,
    alwaysShowHorizontalScroll: true,
    processUnpinnedColumns,
    deltaSort: rowModelType === 'clientSide',
    suppressModelUpdateAfterUpdateTransaction: rowModelType === 'clientSide',
    groupDisplayType: 'multipleColumns',
    stopEditingWhenCellsLoseFocus: true,
    cellSelection: true,
    suppressCutToClipboard: true,
    enableFilterHandlers: true,
    onFloatingFilterUiChanged,
    // needed the destructuring onFilterChanged because otherwise is not executed on change
    onFilterChanged: (params) => onFilterChanged(params),
    ...rest,
  };
};

export const getAgGridFilterConfig = (gridApi: GridApi) => {
  // Defence in depth: gridApi is published from a ref (core/states/agGridTable),
  // so a caller can still be holding a destroyed grid — every method on one
  // returns undefined. ManageFiltersButton calls this during render, so an
  // unguarded read threw and the error boundary blanked the whole page.
  const filterModel = gridApi.getFilterModel() ?? {};

  const sortModel = (gridApi.getColumnState() ?? [])
    .filter((column) => column.sort)
    .map((column) => ({
      colId: column.colId,
      sort: column.sort,
    }));

  return {
    filterModel,
    sortModel,
  };
};

// A fixed-row-height grid keeps the old scroll offset after the row set shrinks and draws
// only the rows under it, leaving the rest blank until the user scrolls.
export const scrollGridToTop = (gridApi: GridApi) => {
  if (gridApi.isDestroyed() || gridApi.getDisplayedRowCount() === 0) {
    return;
  }

  gridApi.ensureIndexVisible(0, 'top');
};

export const updateFloatingFiltersVisibility = (
  columnDefs: ColDef[],
  shouldShow: boolean
): ColDef[] => {
  return columnDefs.map((column) => {
    // A column group's columns show the filter row, not the group.
    if ('children' in column) {
      return {
        ...column,
        children: updateFloatingFiltersVisibility((column as ColGroupDef).children, shouldShow),
      };
    }

    // Preserve columns that explicitly have floatingFilter set to false
    if (column.floatingFilter === false) {
      return {
        ...column,
        floatingFilter: false,
        suppressHeaderFilterButton: true,
      };
    }

    return {
      ...column,
      floatingFilter: shouldShow,
      suppressHeaderFilterButton: !shouldShow,
    };
  });
};

// The default behavior of ag-grid is to unpin the first columns when the grid is resized and
// the columns don't fit in the viewport. This function is used to keep the first pinned columns
// and unpin the last ones.
const processUnpinnedColumns = <T>({ api, columns }: ProcessUnpinnedColumnsParams<T>): Column[] => {
  const reversePinnedColumns = reverse(
    api?.getColumns().filter((column: Column) => column.getPinned() !== null)
  );

  return reversePinnedColumns.slice(0, columns.length);
};

// Automatically apply filter on blur. By default, ag-grid only applies filters on Enter key press,
// so we dispatch an Enter keydown event on blur to trigger filter application.
// Only trigger filtering when the value has actually changed from the current filter value.
const onFloatingFilterUiChanged = ({ api, column }: FloatingFilterUiChangedEvent) => {
  const columnId = column.getColId();
  const columnDef = column.getColDef();

  const inputAriaLabel = `${columnDef.headerName} Filter Input`;

  // Get the DOM element for the floating filter
  const floatingFilterElement = document.querySelector(
    `.ag-floating-filter .ag-floating-filter-input input[aria-label="${inputAriaLabel}"]`
  ) as HTMLInputElement;

  if (floatingFilterElement) {
    floatingFilterElement.onblur = () => {
      const newValue = floatingFilterElement.value;

      // Get the current filter value from the filter model
      const columnFilter = api.getFilterModel()?.[columnId];
      let oldValue = '';

      if (columnFilter) {
        switch (columnFilter?.filterType) {
          case 'text':
            oldValue = columnFilter.filter;
            break;
          case 'number':
            // Convert number to string for comparison since newValue is always a string
            oldValue = columnFilter.filter != null ? String(columnFilter.filter) : '';
            break;
          case 'date':
            oldValue = formatDate(columnFilter.dateFrom, false, DB_DATE_FORMAT);
            break;
        }
      }
      if (newValue !== oldValue) {
        floatingFilterElement.dispatchEvent(
          new KeyboardEvent('keydown', {
            key: 'Enter',
            code: 'Enter',
            bubbles: true,
            cancelable: true,
          })
        );
      }
    };
  }
};
